import { Collapsible } from "@base-ui/react/collapsible";
import { NavigationMenu } from "@base-ui/react/navigation-menu";
import type React from "react";
import { useRef } from "react";
import { IconChevronDown } from "../../icons";
import { ScrollArea } from "../../ScrollArea";
import { MenuSubitem } from "../MenuSubitem";
import { useMenu } from "../menuContext";
import {
  itemContainerClass,
  itemContainerOpenClass,
  itemsListClass,
  menuItemWrapperClass,
  menuModalStyleClass,
  modalHeaderClass,
  styledCollapsiblePanelClass,
  subitesInnerClass,
} from "../styles.css";
import type { MenuItemProps } from "../types";

const SubitemsKeyboardList: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const navKeys = ["ArrowDown", "ArrowUp", "Home", "End"];
    if (!navKeys.includes(e.key)) return;

    const items = Array.from(
      ref.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
    );
    if (items.length === 0) return;

    const active = document.activeElement as HTMLElement | null;
    const idx = active ? items.indexOf(active) : -1;
    let next = idx;
    if (e.key === "ArrowDown") next = idx < 0 ? 0 : (idx + 1) % items.length;
    if (e.key === "ArrowUp")
      next =
        idx < 0 ? items.length - 1 : (idx - 1 + items.length) % items.length;
    if (e.key === "Home") next = 0;
    if (e.key === "End") next = items.length - 1;

    items[next]?.focus();
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      ref={ref}
      role="menu"
      className={itemsListClass}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};

interface MenuItemComponentProps {
  item: MenuItemProps;
  sideBarOpen: boolean;
  categoryOpen: string | null;
  toggleCategoryOpen: (category: string) => void;
}

export const MenuItem: React.FC<MenuItemComponentProps> = ({
  item,
  sideBarOpen,
  categoryOpen,
  toggleCategoryOpen,
}) => {
  const LinkComponent = useMenu();
  const location = window.location.pathname.split("/")[1];

  const hasSubitems = item.subitems && item.subitems.length > 0;
  const pathToGo = item.function || hasSubitems || !item.path ? "#" : item.path;

  const handleItemClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (item.function) item.function();
  };

  const linkProps = {
    id: `menu-item${item.path}`,
    className: `${itemContainerClass} ${
      sideBarOpen ? itemContainerOpenClass : ""
    }`,
    to: pathToGo,
    href: pathToGo,
    "aria-selected": location === item.path?.split("/")[1],
    onClick: handleItemClick,
  };

  if (sideBarOpen) {
    if (hasSubitems) {
      return (
        <div className={menuItemWrapperClass}>
          <Collapsible.Root
            open={categoryOpen === item.text}
            onOpenChange={() => toggleCategoryOpen(item.text)}
          >
            <Collapsible.Trigger
              nativeButton={false}
              render={<LinkComponent {...linkProps} />}
            >
              {item.icon}
              <span>{item.text}</span>
              <IconChevronDown className="chevron" />
            </Collapsible.Trigger>

            <Collapsible.Panel
              className={styledCollapsiblePanelClass}
              keepMounted
            >
              <div className={subitesInnerClass}>
                {item.subitems?.map((subitem) => (
                  <MenuSubitem
                    key={`menu-subitem-${item.text}-${subitem.text}`}
                    subitem={subitem}
                    style={{ marginLeft: 32 }}
                    parentPath={item.path}
                  />
                ))}
              </div>
            </Collapsible.Panel>
          </Collapsible.Root>
        </div>
      );
    }

    return (
      <div className={menuItemWrapperClass}>
        <LinkComponent {...linkProps}>
          {item.icon}
          <span>{item.text}</span>
        </LinkComponent>
      </div>
    );
  }

  // Collapsed state — NavigationMenu handles hover intent
  return (
    <NavigationMenu.Item className={menuItemWrapperClass}>
      {hasSubitems ? (
        <>
          <NavigationMenu.Trigger
            nativeButton={false}
            render={<LinkComponent {...linkProps} />}
          >
            {item.icon}
            <span>{item.text}</span>
          </NavigationMenu.Trigger>
          <NavigationMenu.Content className={menuModalStyleClass}>
            <span className={modalHeaderClass}>{item.text}</span>
            <ScrollArea maxHeight="calc(100vh - 48px)">
              <SubitemsKeyboardList>
                {item.subitems?.map((subitem) => (
                  <MenuSubitem
                    key={`menu-subitem-${item.text}-${subitem.text}`}
                    subitem={subitem}
                    parentPath={item.path}
                    role="menuitem"
                  />
                ))}
              </SubitemsKeyboardList>
            </ScrollArea>
          </NavigationMenu.Content>
        </>
      ) : (
        <NavigationMenu.Link render={<LinkComponent {...linkProps} />}>
          {item.icon}
          <span>{item.text}</span>
        </NavigationMenu.Link>
      )}
    </NavigationMenu.Item>
  );
};
